import Backbone from 'backbone'
import _ from 'lodash'
import FormView from 'scoreboard/components/form-view'
import template from './forecast-model-view.hbs'
import enums from 'scoreboard/components/enums'
import calendars from 'scoreboard/components/calendars'
import dropdown from 'scoreboard/components/dropdown'
import highlight from 'scoreboard/components/highlight'
import i18n from 'scoreboard/components/i18n'
import selects from 'scoreboard/components/selects'

const FORECAST_TYPE = 'forecast-type'
const TREND = 'trend'
const SEASON = 'season'
const SEASON_LENGTH = 'season-length'

/**
 * A dialog for setting a chart line's forecast model options
 */
const ForecastModelForm = Backbone.View.extend({
	tagName: 'form',

	events: {
		'change [name="forecast-type"]': 'onChangeForecastType',
		'change [name="season"]': 'onChangeSeason',
		'change [name="trend"]': 'onChangeTrend'
	},

	/** @type {ChartDesignerForecast} */
	model: null,

	initialize: function(options = {}) {
		this.formView = new FormView({
			el: this.el,
			submit: this.submit.bind(this),
			// The default values don't have to be changed
			allowCleanSubmission: true
		})
		this.dropdowns = []
	},

	render: function() {
		this.$el.html(template(this.context()))

		this.renderSelect(FORECAST_TYPE, 'forecastType',
			this.model.get('forecastType'))
		this.renderSelect(TREND, decompositionOptions(),
			this.model.get('trend'))
		this.renderSelect(SEASON, decompositionOptions(),
			this.model.get('season'))
		this.renderSelect(SEASON_LENGTH, calendars.calendars,
			this.model.get('seasonLengthCalendarId'))

		this.onChangeForecastType()
		this.renderForecastDescription()

		this.formView.start()
		return this
	},

	context: function() {
		return {
			ignoreLastDays: this.model.get('ignoreLastDays'),
			forecastTypeName: FORECAST_TYPE,
			trendName: TREND,
			seasonName: SEASON,
			seasonLengthName: SEASON_LENGTH
		}
	},

	/**
	 * Render an id-based select dropdown using an enum name or predefined list
	 *
	 * @param name the name attribute of the select element to render
	 * @param optionsList a list of options or the name of an enum to create a list from
	 * @param defaultValue the initial value to be selected; if null, the first option
	 * 			in the list is selected
	 */
	renderSelect: function(name, optionsList, defaultValue = null) {
		const options = _.isArray(optionsList) ? optionsList : enums.toOptionData(optionsList)
		this.dropdowns.push(dropdown.idLabels(this.getSelect(name).empty(),
			options, defaultValue || options[0].id))
	},

	renderForecastDescription () {
		let { forecastType, trend, season } = this.val()

		let message
		if (forecastType === 'auto') {
			message = i18n.get('charts.forecast.modelDetails.auto',
				highlight(i18n.get('charts.forecast.ses')),
				highlight(i18n.get('charts.forecast.holtsLinear')),
				highlight(i18n.get('charts.forecast.holtWinters')))
		} else if (forecastType === 'auto-without-seasonality') {
			message = i18n.get('charts.forecast.modelDetails.autoNonSeasonal',
				highlight(i18n.get('charts.forecast.ses')),
				highlight(i18n.get('charts.forecast.holtsLinear')))
		} else if (forecastType === 'custom') {
			let method
			if (trend === 'ignore' && season === 'ignore') {
				method = 'ses'
			} else if (trend === 'additive' && season === 'ignore') {
				method = 'holtsLinear'
			} else if (trend === 'additive' && season === 'additive') {
				method = 'holtWinters'
			}

			if (method) {
				message = i18n.get('charts.forecast.modelDetails.custom',
					highlight(i18n.get('charts.forecast.' + method)))
			}
		}

		this.$('.forecast-model-description')
			.html(message)
			.toggleClass('is-not-displayed', !message)
	},

	/**
	 * @return {Object} a jquery select object
	 */
	getSelect: function(name) {
		return this.$(`[name="${name}"]`)
	},

	/**
	 * @return {Object} the label container for a select
	 */
	getSelectContainer: function(name) {
		return this.$(`label.${name}`)
	},

	onChangeForecastType: function() {
		const hide = this.getSelect(FORECAST_TYPE).val() !== 'custom'
		// Only show the trend and season dropdown if the forecast model is set
		// to custom
		this.getSelectContainer(TREND).toggleClass('is-not-displayed', hide)
		this.getSelectContainer(SEASON).toggleClass('is-not-displayed', hide)
		this.onChangeSeason()
		this.renderForecastDescription()
	},

	onChangeSeason: function() {
		const modelValue = this.getSelect(FORECAST_TYPE).val()
		const seasonValue = this.getSelect(SEASON).val()

		// Only show the season length dropdown if the forecast model is set to
		// custom and the season value is not 'ignore'
		const hide = modelValue !== 'custom' || seasonValue === 'ignore'
		this.getSelectContainer(SEASON_LENGTH).toggleClass('is-not-displayed', hide)

		// Current forecasting code doesn't support combination of trend=ignore
		// and season=additive, so if season is set to additive, set the trend
		// to additive and disable trend input
		let forceAdditiveTrend = seasonValue === 'additive'
		let trendSelect = this.getSelect(TREND)

		if (forceAdditiveTrend) {
			trendSelect.val(forceAdditiveTrend ? 'additive' : 'ignore')
		}
		trendSelect.prop('disabled', forceAdditiveTrend)
		selects(trendSelect)
		this.renderForecastDescription()
	},

	onChangeTrend: function () {
		this.renderForecastDescription()
	},

	val: function () {
		const forecastType = this.getSelect(FORECAST_TYPE).val()
		const isCustom = forecastType === 'custom'
		const isNotIgnore = this.getSelect(SEASON).val() !== 'ignore'
		const ignoreLastDays = this.$('[name="ignore-last-days"]').val()

		return {
			id: this.model.id,
			forecastType: forecastType,
			trend: isCustom ? this.getSelect(TREND).val() : null,
			season: isCustom ? this.getSelect(SEASON).val() : null,
			seasonLengthCalendarId: isCustom && isNotIgnore ?
				this.getSelect(SEASON_LENGTH).val() : null,
			ignoreLastDays: ignoreLastDays ? parseInt(ignoreLastDays) : null
		}
	},

	submit: function() {
		this.trigger('done', this.val())
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap(this.dropdowns, 'remove')
	}
})

function decompositionOptions () {
	// Multiplicative not yet supported
	return enums.toOptionData("chartForecastDecomposition")
		.filter(option => option.id !== 'multiplicative')
}

export default ForecastModelForm

ForecastModelForm.__filename__ = 'charts/edit/serie/forecast-model-view.js'
